import React, { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Grid,
} from '@mui/material';
import { IconButton } from '@components/iconButton';
import { ChevronLeft, ChevronRight } from '@assets/svgs';
import { arraysOverlap } from '@utils/arrays';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { WIDTH_CONSTANTS } from '@theme/widthConstants';
import { Tooltip } from '@components/tooltip/Tooltip';

export type AdminDrawerListItem = {
  name: string;
  icon?: ReactNode;
  path?: string;
  roles: string[];
  isDisabled?: boolean;
};

interface IAdminDrawer {
  items?: AdminDrawerListItem[];
  toggleDrawer?: (name: string | null) => void;
}

const COLLAPSED_WIDTH = WIDTH_CONSTANTS.SIDEBAR_WIDTH; // 52px
const EXPANDED_WIDTH = 250;

export const AdminDrawer = React.memo<IAdminDrawer>(({ items, toggleDrawer }) => {
  const [roles] = useStore((state) => [state.roles], shallow);
  const [isAdminNavExpanded, setIsAdminNavExpanded] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Auto-collapse when navigating to brokerage management
  useEffect(() => {
    if (
      (location.pathname.includes('brokerage-management') ||
        location.pathname.includes('advanced-portal-configuration')) &&
      isAdminNavExpanded
    ) {
      setIsAdminNavExpanded(false);
    }
  }, [location.pathname, isAdminNavExpanded, setIsAdminNavExpanded]);

  const handleItemClick = (item: AdminDrawerListItem) => {
    if (item.isDisabled) return;

    if (item.path) {
      navigate(item.path);
    }

    if (toggleDrawer) {
      toggleDrawer(item.name);
    }
  };

  const handleToggleExpansion = () => {
    setIsAdminNavExpanded(!isAdminNavExpanded);
  };

  const currentWidth = isAdminNavExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH;

  // Hide toggle button on routes where navigation can't be expanded
  const shouldShowToggleButton =
    !location.pathname.includes('brokerage-management') && !location.pathname.includes('advanced-portal-configuration');

  return (
    <Box
      sx={{
        position: 'relative',
        width: `${currentWidth}px`,
        minWidth: `${currentWidth}px`,
        display: 'flex',
        flexDirection: 'row',
        transition: 'width 0.2s ease-in-out',
        height: '100%',
        flexShrink: 0,
      }}
    >
      <MuiDrawer
        variant='permanent'
        anchor='left'
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'drawerNavigation.main',
              height: '100%',
              width: `${currentWidth}px`,
              borderRadius: '0',
              boxShadow: 'none',
              position: 'relative',
              left: 0,
              right: 0,
              zIndex: 1,
              overflowX: 'hidden',
              overflowY: 'auto',
              transition: 'width 0.2s ease-in-out',
              border: 'none',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            },
          },
        }}
      >
        <List sx={{ margin: '0', padding: '0' }}>
          {items
            ?.filter((i) => !!i.icon && arraysOverlap(i.roles, roles))
            .map((item: AdminDrawerListItem) => {
              const isSelected = item.path && location.pathname.includes(item.path);

              return (
                <ListItem
                  key={item.name}
                  disablePadding
                  sx={{
                    display: 'block',
                    marginTop: '10px',
                  }}
                >
                  <ListItemButton
                    onClick={() => handleItemClick(item)}
                    disabled={item.isDisabled}
                    sx={{
                      height: 36,
                      width: isAdminNavExpanded ? 'auto' : 36,
                      justifyContent: isAdminNavExpanded ? 'initial' : 'center',
                      backgroundColor: isSelected ? 'button.outlinedDark' : 'transparent',
                      color: isSelected ? 'button.outlined' : 'text.main',
                      fill: isSelected ? 'button.outlined' : 'text.main',
                      borderRadius: isAdminNavExpanded ? '8px' : '80px',
                      margin: '0 auto',
                      '&:hover': {
                        backgroundColor: isSelected ? 'button.outlinedDark' : 'background.main',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.5,
                      },
                    }}
                  >
                    <Tooltip title={isAdminNavExpanded ? '' : item.name} placement='right'>
                      <ListItemIcon
                        sx={
                          isAdminNavExpanded
                            ? {
                                minWidth: 24,
                                left: 6,
                                position: 'absolute',
                                color: 'inherit',
                                '& svg': { color: 'inherit', fill: 'black' },
                              }
                            : {
                                position: 'absolute',
                                inset: 0,
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'inherit',
                                '& svg': { color: 'inherit', fill: 'black' },
                              }
                        }
                      >
                        {item.icon}
                      </ListItemIcon>
                    </Tooltip>
                    <ListItemText
                      primary={item.name}
                      sx={{
                        left: 26,
                        position: 'relative',
                        opacity: isAdminNavExpanded ? 1 : 0,
                        '& .MuiListItemText-primary': {
                          fontSize: '14px',
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? 'button.outlined' : 'text.main',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
        </List>

        {/* Toggle button at bottom */}
        {shouldShowToggleButton && (
          <Grid
            container
            sx={{
              justifyContent: isAdminNavExpanded ? 'flex-end' : 'center',
              alignItems: 'center',
              marginTop: 'auto',
              minHeight: '80px',
              paddingRight: isAdminNavExpanded ? '16px' : '0',
              gap: 2,
            }}
          >
            <IconButton
              variant='circular'
              onClick={handleToggleExpansion}
              sx={{
                color: 'text.main',
                '&:hover': {
                  backgroundColor: 'background.main',
                },
              }}
            >
              {isAdminNavExpanded ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>
          </Grid>
        )}
      </MuiDrawer>
    </Box>
  );
});

AdminDrawer.displayName = 'AdminDrawer';
